/**
 * Invisible Labor Appendix Slide
 * Dominant donut chart + detailed 19-role table with full cost/hours breakdown
 * Source: Insure.com Mother's Day Index 2025, BLS wage data
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Color palette: yellow → orange → coral → purple → magenta
// ============================================

const PALETTE = {
  yellow: colors.accent.yellow.exxtraDark,
  orange: colors.accent.orange.exxtraDark,
  coral: colors.accent.coral.exxtraDark,
  purple: colors.accent.purple.exxtraDark,
  magenta: colors.accent.magenta.exxtraDark,
};

// ============================================
// Data
// ============================================

interface Role {
  name: string;
  hrsWk: number;
  annualValue: number;
  yoy: string;
}

interface Category {
  label: string;
  shortLabel: string;
  color: string;
  roles: Role[];
}

const CATEGORIES: Category[] = [
  {
    label: 'Education & Teaching',
    shortLabel: 'Education',
    color: PALETTE.yellow,
    roles: [
      { name: 'Elementary school teacher', hrsWk: 20, annualValue: 24156, yoy: '-1%' },
      { name: 'Other teachers & instructors', hrsWk: 10, annualValue: 13768, yoy: '+2%' },
    ],
  },
  {
    label: 'Childcare & Personal Care',
    shortLabel: 'Childcare',
    color: PALETTE.orange,
    roles: [
      { name: 'Childcare worker', hrsWk: 40, annualValue: 33134, yoy: '+3%' },
      { name: 'Personal care aide', hrsWk: 3, annualValue: 2624, yoy: '+5%' },
    ],
  },
  {
    label: 'Emotional Labor & Advocacy',
    shortLabel: 'Emotional',
    color: PALETTE.coral,
    roles: [
      { name: 'Community & social service specialist', hrsWk: 40, annualValue: 13469, yoy: '+8%' },
      { name: 'Judge / magistrate (conflict resolution)', hrsWk: 3, annualValue: 10320, yoy: '+3%' },
      { name: 'Mental health counselor', hrsWk: 5, annualValue: 5584, yoy: '+11%' },
    ],
  },
  {
    label: 'Household & Food',
    shortLabel: 'Household',
    color: PALETTE.purple,
    roles: [
      { name: 'Cook (meal planning & prep)', hrsWk: 14, annualValue: 12478, yoy: '+5%' },
      { name: 'Maid / housekeeper', hrsWk: 10, annualValue: 9043, yoy: '+4%' },
      { name: 'Laundry worker', hrsWk: 4, annualValue: 3268, yoy: '+7%' },
      { name: 'Other designers (home décor)', hrsWk: 5, annualValue: 1500, yoy: '+3%' },
      { name: 'Landscaping / groundskeeping', hrsWk: 1, annualValue: 1022, yoy: '+3%' },
      { name: 'Hairdresser / cosmetologist', hrsWk: 0.5, annualValue: 543, yoy: '+4%' },
      { name: 'Baker', hrsWk: 3, annualValue: 435, yoy: '+6%' },
    ],
  },
  {
    label: 'Logistics & Admin',
    shortLabel: 'Logistics',
    color: PALETTE.magenta,
    roles: [
      { name: 'Chauffeur / shuttle driver', hrsWk: 9, annualValue: 8794, yoy: '+6%' },
      { name: 'Meeting & convention planner', hrsWk: 8, annualValue: 2003, yoy: '+5%' },
      { name: 'Private detective / investigator', hrsWk: 5, annualValue: 1186, yoy: '+2%' },
      { name: 'Accountant / auditor', hrsWk: 0.5, annualValue: 1169, yoy: '+3%' },
      { name: 'Licensed practical nurse', hrsWk: 2, annualValue: 740, yoy: '+6%' },
    ],
  },
];

const getCategoryTotal = (cat: Category) =>
  cat.roles.reduce((sum, r) => sum + r.annualValue, 0);
const getCategoryHours = (cat: Category) =>
  cat.roles.reduce((sum, r) => sum + r.hrsWk, 0);
const GRAND_TOTAL = CATEGORIES.reduce((sum, c) => sum + getCategoryTotal(c), 0);

// ============================================
// Content: donut left, table right
// ============================================

const ChartPanel = styled.div`
  flex: 0 0 38%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  gap: ${spacing.sm};
`;

const TablePanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

// ============================================
// Legend under donut
// ============================================

const LegendList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const LegendLabel = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.8vw, ${typography.fontSize.xs});
  color: ${colors.highlight.primary};
  flex: 1;
`;

const LegendValue = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 0.8vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.highlight.dark};
  text-align: right;
  min-width: 44px;
`;

const LegendPct = styled.span`
  font-weight: 400;
  color: ${colors.highlight.light};
  margin-left: 4px;
`;

// ============================================
// Role table
// ============================================

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 52px 72px 42px;
  gap: 4px;
  padding: 3px ${spacing.xs};
  border-bottom: 1.5px solid ${colors.neutral.light};
  margin-bottom: 2px;
`;

const ColHead = styled.div<{ $align?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(8px, 0.8vw, ${typography.fontSize.xxs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: ${(p) => p.$align || 'left'};
`;

const CategoryHeader = styled.div<{ $color: string }>`
  display: grid;
  grid-template-columns: 1fr 52px 72px 42px;
  gap: 4px;
  padding: 3px ${spacing.xs};
  margin-top: 3px;
  border-left: 3px solid ${(p) => p.$color};
  background: ${(p) => p.$color}12;
`;

const CatLabel = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 0.85vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

const CatValue = styled.div<{ $align?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 0.85vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.highlight.dark};
  text-align: ${(p) => p.$align || 'right'};
`;

const RoleRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 52px 72px 42px;
  gap: 4px;
  padding: 2px ${spacing.xs} 2px calc(${spacing.xs} + 3px);
  border-bottom: 1px solid ${colors.neutral.extraLight};

  &:last-child {
    border-bottom: none;
  }
`;

const RoleName = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoleValue = styled.div<{ $align?: string }>`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  text-align: ${(p) => p.$align || 'right'};
`;

const YoyUp = styled(RoleValue)`
  color: ${colors.accent.coral.exxtraDark};
`;

const YoyDown = styled(RoleValue)`
  color: ${colors.accent.green.exxtraDark};
`;

// ============================================
// Footer note
// ============================================

const FooterNote = styled.div`
  flex-shrink: 0;
  font-family: ${typography.fontFamily.body};
  font-size: clamp(8px, 0.8vw, ${typography.fontSize.xxs});
  color: ${colors.highlight.light};
  line-height: 1.4;
  padding: ${spacing.xs} ${spacing.sm};
  margin-top: ${spacing.xs};
  background: ${colors.neutral.ultraLight};
  border-radius: ${borderRadius.sm};
  border-left: 3px solid ${colors.accent.coral.exxtraDark};
`;

const FooterBold = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.accent.coral.exxtraDark};
`;

const Source = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(7px, 0.65vw, ${typography.fontSize.xxs});
  color: ${colors.highlight.extraLight};
  text-align: center;
  margin-top: 3px;
`;

// ============================================
// SVG Donut Chart
// ============================================

const DonutChart: React.FC = () => {
  const R = 100;
  const STROKE = 40;
  const circumference = 2 * Math.PI * R;
  let offset = 0;

  return (
    <svg viewBox="-150 -150 300 300" style={{ width: '100%', maxWidth: 300, height: 'auto' }}>
      {CATEGORIES.map((cat) => {
        const total = getCategoryTotal(cat);
        const pct = total / GRAND_TOTAL;
        const dash = pct * circumference;
        const gap = circumference - dash;
        const currentOffset = offset;
        offset += dash;

        return (
          <circle
            key={cat.label}
            r={R}
            cx={0}
            cy={0}
            fill="none"
            stroke={cat.color}
            strokeWidth={STROKE}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-currentOffset}
            transform="rotate(-90)"
          />
        );
      })}
      {/* Center: total value */}
      <text
        textAnchor="middle"
        dominantBaseline="central"
        y={-16}
        fontFamily="'Poppins', sans-serif"
        fontSize={32}
        fontWeight={700}
        fill={colors.highlight.dark}
      >
        $145K
      </text>
      <text
        textAnchor="middle"
        dominantBaseline="central"
        y={8}
        fontFamily="'Poppins', sans-serif"
        fontSize={12}
        fontWeight={500}
        fill={colors.highlight.light}
      >
        replacement cost/yr
      </text>
      <text
        textAnchor="middle"
        dominantBaseline="central"
        y={24}
        fontFamily="'Poppins', sans-serif"
        fontSize={10}
        fontWeight={400}
        fill={colors.highlight.extraLight}
      >
        19 roles &middot; 183 hrs/wk
      </text>
    </svg>
  );
};

// ============================================
// Helpers
// ============================================

const fmtDollars = (v: number) => {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${v}`;
};

// ============================================
// Component
// ============================================

export const InvisibleLaborSlide: React.FC = () => (
  <AppendixSlideLayout>
    <SlideHeader
      title="The Invisible Labor Index"
      subtitle="Market-rate replacement cost of what moms do · Insure.com 2025 · BLS wage data"
    />

    <AppendixContentArea>
      <ChartPanel>
        <DonutChart />
        <LegendList>
          {CATEGORIES.map((cat) => {
            const total = getCategoryTotal(cat);
            return (
              <LegendRow key={cat.label}>
                <LegendDot $color={cat.color} />
                <LegendLabel>{cat.shortLabel}</LegendLabel>
                <LegendValue>
                  {fmtDollars(total)}
                  <LegendPct>{Math.round((total / GRAND_TOTAL) * 100)}%</LegendPct>
                </LegendValue>
              </LegendRow>
            );
          })}
        </LegendList>
      </ChartPanel>

      <TablePanel>
        <TableHeader>
          <ColHead>Role</ColHead>
          <ColHead $align="right">Hrs/Wk</ColHead>
          <ColHead $align="right">Annual Value</ColHead>
          <ColHead $align="right">YoY</ColHead>
        </TableHeader>

        {CATEGORIES.map((cat) => (
          <React.Fragment key={cat.label}>
            <CategoryHeader $color={cat.color}>
              <CatLabel>{cat.label}</CatLabel>
              <CatValue $align="right">
                {getCategoryHours(cat)}
              </CatValue>
              <CatValue $align="right">
                {fmtDollars(getCategoryTotal(cat))}
              </CatValue>
              <CatValue $align="right">
                {Math.round((getCategoryTotal(cat) / GRAND_TOTAL) * 100)}%
              </CatValue>
            </CategoryHeader>
            {cat.roles.map((role) => {
              const YoyCell = role.yoy.startsWith('+') ? YoyUp : YoyDown;
              return (
                <RoleRow key={role.name}>
                  <RoleName>{role.name}</RoleName>
                  <RoleValue $align="right">{role.hrsWk}</RoleValue>
                  <RoleValue $align="right">
                    ${role.annualValue.toLocaleString()}
                  </RoleValue>
                  <YoyCell $align="right">{role.yoy}</YoyCell>
                </RoleRow>
              );
            })}
          </React.Fragment>
        ))}
      </TablePanel>
    </AppendixContentArea>

    <FooterNote>
      <FooterBold>Not counted:</FooterBold> Household project management (the cognitive labor of
      coordinating all 19 roles), IT/tech admin, logistics coordination, procurement &amp;
      purchasing, pet care, and home maintenance coordination &mdash; precisely the{' '}
      <FooterBold>cognitive tasks</FooterBold> that are hardest to see and hardest to outsource.
      The 183 hrs/wk total reflects overlapping roles (teaching while cooking while counseling).
      The $145K figure is the market-rate replacement cost, not a literal time allocation.
    </FooterNote>
    <Source>
      Source: Insure.com Mother&rsquo;s Day Index 2025 (BLS mean hourly wage data) &middot; +4% YoY average
    </Source>
  </AppendixSlideLayout>
);
